/**
 * @file dates.tx
 *
 * @fileoverview Utils for date manipulation.
 */
import { format, parseISO } from 'date-fns';
import { IntlShape } from 'react-intl';

/**
 * This is a JavaScript function that returns a date format string based on the current date and a specified language.
 * It uses the Intl.DateTimeFormat object to format the date and then maps over the resulting object to extract the relevant
 * parts of the date (day, month, and year).
 *
 * The function then returns a concatenated string of these parts in the desired format (e.g. "dd/MM/yyyy").
 * @param lang
 *  The locale to format the date in.
 * @param removeDay
 *  If the string format should remove the day part of the date.
 *
 * @returns string
 *  a concatenated string of these parts in the desired format (e.g. "dd/MM/yyyy").
 *
 */
function getDateFormatString(lang = 'default', removeDay = false) {
  let options: any = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };

  if (removeDay) {
    options = {
      year: 'numeric',
      month: 'numeric',
    };
  }

  const formatObj = new Intl.DateTimeFormat(lang, options).formatToParts(
    new Date(),
  );

  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        default:
          return obj.value;
      }
    })
    .join('');
}

/**
 * Formats date and removes the day.
 *
 * @param {string | Date} date
 *  Date to remove day from.
 * @param {IntlShape} intl
 *  Intl.
 * @returns {Date | string}
 *  Date without day.
 */
export const removeDayFromDate = (
  date: string | Date,
  intl: IntlShape,
): Date | string => {
  try {
    let dateFormat = deriveDateFormat(intl, true);

    if (typeof date === 'string') {
      date = new Date(Date.parse(date));
    }

    return format(date, dateFormat);
  } catch (error) {
    // If we encounter an error, return an unchanged value.
    return date;
  }
};

/**
 * Returns the format of dates in the current locale.
 *
 * @param {IntlShape} intl
 *  Intl.
 * @param {boolean} removeDay
 *  Flag indicating if day should be removed from the format.
 * @returns {string}
 *  Date format string.
 */
export const deriveDateFormat = (
  intl: IntlShape,
  removeDateDay?: boolean,
): string => {
  return getDateFormatString(intl.locale, removeDateDay);
};

/**
 * Parse the given string in ISO 8601 format and return an instance of Date
 *
 * @param date
 *  The date to be parsed.
 * @returns
 *  The formatted Date object in ISO 8601.
 */
export function parseUniversalDate(date) {
  return parseISO(date);
}
